import Looping from '@/designs-dev/handover/Looping';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import { MENU_FOLD_MS } from '@/karehero-app/tell-us-once/intake/ChatMenuMessage';
import {
  HEARD_MS,
  PIN_LEAVE_MS,
  PIN_RETURN_DELAY_MS,
  PIN_RETURN_MS,
  PIN_SCRIM_MS,
  STAGE_MS,
  THINK_MS,
} from '@/karehero-app/tell-us-once/intake/ChatV2';
import { INTAKE_CHAT_V2_0_DEV } from '@/karehero-app/tell-us-once/intake/content.v2-0-dev';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';
import { LINE_MS } from '@/karehero-app/tell-us-once/intake/ReadingBubble';

// 2.0 on dev, opened on a frame that plays its own movement.
const onDev = (initial: Parameters<typeof Flow>[0]['initial']) => (
  <Flow initial={initial} option={2} version='2.0-dev' />
);

// How long a question's reading takes, from the chat itself.
const readMs = (id: string) =>
  INTAKE_CHAT_V2_0_DEV.find((q) => q.id === id)?.reading?.ms ?? 0;

const s = (ms: number) =>
  ms >= 1000 ? `${Number((ms / 1000).toFixed(1))}s` : `${ms}ms`;

/**
 * What moves in 2.0, and how long it takes. Every loop is the prototype
 * playing itself, and every timing in its line is read from the constant the
 * prototype uses, so it cannot drift from the thing it describes.
 */
export const interactions20Spec: HandoverSpec = {
  id: 'interactions-2-0',
  title: 'Interactions',
  description:
    "What moves in 2.0's conversation and how long it takes. Each plays on a loop.",
  links: [
    { label: 'Patterns', url: '/tell-us-once/flows/onboarding-patterns' },
    { label: 'Onboarding 2.0', url: '/tell-us-once/flows/onboarding-2-0' },
  ],
  flows: [],
  interactions: [
    {
      id: 'reply-beat',
      name: 'The beat before a reply',
      what: `Their answer, then ${s(THINK_MS)} of dots before the reply.`,
      render: () => (
        <Looping every={4500} remount render={() => onDev('help-how-yes')} />
      ),
    },
    {
      id: 'reading-lines',
      name: 'Going through what they shared',
      what: `A line at a time while their answer is read. ${s(LINE_MS)} each, ${s(readMs('help'))} in all.`,
      render: () => (
        <Looping
          every={readMs('help') + 600}
          remount
          render={() => onDev('help-processing')}
        />
      ),
    },
    {
      id: 'ticks-while-recording',
      name: 'Ticking off as they talk',
      what: `The prompts tick off as they talk, one every ${s(HEARD_MS)}.`,
      render: () => (
        <Looping
          every={1500 + HEARD_MS * 4}
          remount
          render={() => onDev('help-ask-starts-recording')}
        />
      ),
    },
    {
      id: 'bar-changes',
      name: 'The bar changes with the question',
      what: `The bar goes from typing to choosing and back, over ${s(STAGE_MS)}.`,
      render: () => (
        <Looping
          render={(onReplay) => (
            <Flow
              initial='help-age'
              option={2}
              version='2.0-dev'
              onReplay={onReplay}
            />
          )}
        />
      ),
    },
    {
      id: 'card-lets-go',
      name: 'The card lets go',
      what: `After I'm done, the pinned card presses down and slides away, over ${s(PIN_LEAVE_MS)}.`,
      render: () => (
        <Looping
          every={5000}
          remount
          render={() => onDev('help-gather-done')}
        />
      ),
    },
    {
      id: 'card-pins-again',
      name: 'The card comes back',
      what: `Recording with something still missing brings the card back: a ${s(PIN_RETURN_DELAY_MS)} wait, then ${s(PIN_RETURN_MS)}.`,
      render: () => (
        <Looping
          every={5000}
          remount
          render={() => (
            <Flow
              initial='help-summary-starts-recording'
              option={2}
              version='2.0-dev'
              branch='missing'
            />
          )}
        />
      ),
    },
    {
      id: 'menu-folds',
      name: 'Down to the one they picked',
      what: `The rest fold away over ${s(MENU_FOLD_MS)}, leaving the one they picked.`,
      render: () => (
        <Looping
          every={4200}
          remount
          render={() => (
            <Flow initial='help-start-picking' option={2} version='2.0' />
          )}
        />
      ),
    },
  ],
  states: [],
  components: {},
};
